"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _tssReact = require("tss-react");
var _createStyles = require("../styles/createStyles");
var _useTheme = require("../styles/useTheme");
var _utilities = require("../utilities");
const _excluded = ["classes", "className", "color", "size", "value", "variant"];
const TRANSITION_DURATION = 4; // seconds

// Legends:
// || represents the viewport
// -  represents a light background
// x  represents a dark background
// 0% : //  |-----|---x-||-----||-----|
// 60% : //  |-----|-----||-----||xxxx-|
const indeterminate1Keyframes = (0, _tssReact.keyframes)`
  0% {
    left: -35%;
    right: 100%;
  }

  60% {
    left: 100%;
    right: -90%;
  }

  100% {
    left: 100%;
    right: -90%;
  }
`;

// 0% : //  |xxxxx|xxxxx||-----||-----|
// 60% : //  |-----|-----||-----||-x----|
const indeterminate2Keyframes = (0, _tssReact.keyframes)`
  0% {
    left: -200%;
    right: 100%;
  }

  60% {
    left: 107%;
    right: -8%;
  }

  100% {
    left: 107%;
    right: -8%;
  }
`;
const useStyles = (0, _createStyles.createStyles)(_ref => {
  let {
    theme
  } = _ref;
  const {
    styles,
    colors,
    spacing
  } = theme.busbud;
  return {
    /* Styles applied to the root element. */
    root: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: styles.rounding.sm
    },
    sizeSm: {
      height: spacing(0.75)
    },
    sizeMd: {
      height: spacing(1)
    },
    /* Styles applied to the root and bar2 element if `color="primary"` */
    colorPrimary: {
      backgroundColor: colors.component.progressBar.primary.track
    },
    /* Styles applied to the root and bar2 elements if `color="secondary"` */
    colorSecondary: {
      backgroundColor: colors.component.progressBar.secondary.track
    },
    /* Styles applied to the root element if `variant="determinate"`. */
    determinate: {},
    /* Styles applied to the root element if `variant="indeterminate"`. */
    indeterminate: {},
    /* Styles applied to the root element if `variant="query"`. */
    query: {
      transform: 'rotate(180deg)'
    },
    /* Styles applied to the layered bar1 and bar2 elements. */
    bar: {
      width: '100%',
      position: 'absolute',
      left: 0,
      bottom: 0,
      top: 0,
      transition: 'transform 0.2s linear',
      transformOrigin: 'left',
      borderRadius: styles.rounding.sm
    },
    /* Styles applied to the bar elements if `color="primary"` */
    barColorPrimary: {
      backgroundColor: colors.component.progressBar.primary.progress
    },
    /* Styles applied to the bar elements if `color="secondary"` */
    barColorSecondary: {
      backgroundColor: colors.component.progressBar.secondary.progress
    },
    /* Styles applied to the bar1 element if `variant="indeterminate or query"`. */
    bar1Indeterminate: {
      width: 'auto',
      animation: `${indeterminate1Keyframes} 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite`
    },
    /* Styles applied to the bar1 element if `variant="determinate"`. */
    bar1Determinate: {
      transition: `transform .${TRANSITION_DURATION}s linear`
    },
    /* Styles applied to the bar2 element if `variant="indeterminate or query"`. */
    bar2Indeterminate: {
      width: 'auto',
      animation: `${indeterminate2Keyframes} 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite`,
      animationDelay: '1.15s'
    }
  };
});
/**
 * LinearProgress informs users about the status of ongoing processes, such as loading an app, submitting a form, or saving updates.
 *
 *  - Determinate indicators display how long an operation will take.
 *  - Indeterminate indicators visualize an unspecified wait time.
 *
 *  [Storybook](https://busbud.github.io/design-system/?path=/docs/components-feedback-linearprogress--page)
 */
const LinearProgress = /*#__PURE__*/_react.default.forwardRef(function LinearProgress(props, ref) {
  const {
      classes: classesOverrides,
      className,
      color = 'primary',
      size = 'md',
      value,
      variant = 'indeterminate'
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded);
  const theme = (0, _useTheme.useTheme)();
  const {
    cx,
    classes
  } = useStyles({
    classesOverrides
  });
  const rootProps = {};
  const inlineStyles = {
    bar1: {},
    bar2: {}
  };
  if (variant === 'determinate') {
    if (value !== undefined) {
      rootProps['aria-valuenow'] = Math.round(value);
      let transform = value - 100;
      if (theme.direction === 'rtl') {
        transform = -transform;
      }
      inlineStyles.bar1.transform = `translateX(${transform}%)`;
    } else if (process.env.NODE_ENV !== 'production') {
      console.error('DSCL: you need to provide a value prop ' + 'when using the determinate variant of LinearProgress .');
    }
  }
  return /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    className: cx(classes.root, classes[`size${(0, _utilities.capitalizeFirstWord)(size)}`], classes[`color${(0, _utilities.capitalizeFirstWord)(color)}`], {
      [classes.determinate]: variant === 'determinate',
      [classes.indeterminate]: variant === 'indeterminate',
      [classes.query]: variant === 'query'
    }, className),
    role: "progressbar"
  }, rootProps, {
    ref: ref
  }, other), /*#__PURE__*/_react.default.createElement("div", {
    className: cx(classes.bar, classes[`barColor${(0, _utilities.capitalizeFirstWord)(color)}`], {
      [classes.bar1Indeterminate]: variant === 'indeterminate' || variant === 'query',
      [classes.bar1Determinate]: variant === 'determinate'
    }),
    style: inlineStyles.bar1
  }), variant === 'determinate' ? null : /*#__PURE__*/_react.default.createElement("div", {
    className: cx(classes.bar, [classes[`barColor${(0, _utilities.capitalizeFirstWord)(color)}`]], {
      [classes.bar2Indeterminate]: variant === 'indeterminate' || variant === 'query'
    }),
    style: inlineStyles.bar2
  }));
});
var _default = exports.default = LinearProgress;